import {
  Cell,
  SpreadsheetData,
} from "@via/components/spreadsheet-editor/spreadsheet-editor.tsx";
import {
  type DataSource,
  DataSourceProvider,
  FileProvider,
  type SaveFileRequestBody,
} from "common";
import { IFileOrganizerBackupForm } from "@via/pages/data/file-organizer/file-organizer-backup-form.tsx";

/**
 * Formats fetched JSON data into SpreadsheetData structure
 * based on provided column labels.
 *
 * - Matches keys case-insensitively.
 * - Keeps consistent number of columns.
 * - Stringifies all values.
 * - If key is missing, uses an empty cell.
 */
export function formatSpreadsheetData(
  columnLabels: string[],
  fetchedData: Record<string, any>[]
): SpreadsheetData {
  const normalizedLabels = columnLabels.map(label => label.toLowerCase());

  return fetchedData.map(rowObj => {
    // map lowercase -> original key
    const lowerKeys = Object.keys(rowObj).reduce<Record<string, string>>(
      (acc, key) => {
        acc[key.toLowerCase()] = key;
        return acc;
      },
      {}
    );

    // build cells in column order
    const cells: Cell[] = normalizedLabels.map(label => {
      const matchedKey = lowerKeys[label];
      const rawValue =
        matchedKey !== undefined ? rowObj[matchedKey] : undefined;
      return { value: rawValue != null ? String(rawValue) : "" };
    });

    return cells;
  });
}

/**
 * Converts SpreadsheetData (2D array) back into an array of objects.
 *
 * - Keeps column names exactly as displayed (case-sensitive).
 * - Includes new user-added columns.
 * - Empty cells become empty strings ("").
 * - Keeps all rows (no filtering).
 * - All values remain strings.
 */
export function spreadsheetToObjects(
  columnLabels: string[],
  spreadsheetData: SpreadsheetData
): any[] {
  return spreadsheetData.map(row => {
    const rowObj: Record<string, string> = {};

    columnLabels.forEach((label, colIndex) => {
      const cell = row[colIndex];
      rowObj[label] = cell?.value != null ? String(cell.value) : "";
    });

    return rowObj;
  });
}

export function filterColumnsByCollectionType(
  columns: string[],
  collectionType: string,
  provider: DataSourceProvider,
  fileFields?: FileFieldNameLabel[] | null
): string[] {
  const baseExcluded = excludedColumnLabels[collectionType] ?? [];
  const excluded = [...baseExcluded];
  // The system source fields are a snapshot the add flow fills in, never a column to type into.
  // Hidden while the field list loads, and whenever the labels are the system fields. A user's
  // own field that happens to use one of the labels stays visible.
  if (fileFields === undefined || hasSystemSourceFields(fileFields)) {
    excluded.push(SOURCE_NAME_LABEL, SOURCE_LABEL);
  }
  if (provider === DataSourceProvider.GEO) {
    // For GEO provider, exclude file1, file2, file3, file4, and providerIds
    excluded.push("File1", "File2", "File3", "File4", "Provider IDs");
  } else {
    // For non-GEO providers, exclude GEO ID
    excluded.push("GEO ID");
    // For non-BaseSpace providers, exclude Provider IDs
    if (provider !== DataSourceProvider.BaseSpace) {
      excluded.push("Provider IDs");
    }
  }
  return columns.filter(col => !excluded.includes(col));
}

export const excludedColumnLabels = {
  single: [
    "File2",
    "File3",
    "File4",
    "File Layout",
    "Archive Directory",
    "Google Storage Archive Directory",
    "S3 Archive Directory",
  ],
  pair: [
    "File3",
    "File4",
    "File Layout",
    "Archive Directory",
    "Google Storage Archive Directory",
    "S3 Archive Directory",
  ],
  triple: [
    "File4",
    "File Layout",
    "Archive Directory",
    "Google Storage Archive Directory",
    "S3 Archive Directory",
  ],
};

/**
 * Converts empty strings to null values in an object.
 * This is useful before sending data to the backend, as empty strings
 * should typically be represented as null in the database.
 *
 * @param obj - The object to transform
 * @returns A new object with empty strings converted to null
 */
export function convertEmptyStringsToNull<T extends Record<string, unknown>>(
  obj: T
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (value === "") {
      result[key] = null;
    } else if (
      typeof value === "object" &&
      value !== null &&
      !Array.isArray(value)
    ) {
      // Recursively process nested objects
      result[key] = convertEmptyStringsToNull(value as Record<string, unknown>);
    } else {
      result[key] = value;
    }
  }

  return result;
}

/**
 * Maps backup form data to file object format for vmeta dataset files.
 * Stores archive directories and credential IDs in separate fields.
 *
 * @param backupFormData - The backup form data from FileOrganizerBackupForm
 * @returns An object with archive directory fields formatted for the file object
 */
export function mapBackupFormDataToFileObject(
  backupFormData: IFileOrganizerBackupForm | undefined
): Record<string, unknown> {
  if (!backupFormData) {
    return {};
  }

  const fileArchiveFields: Record<string, unknown> = {};

  // Add local archive directory
  if (backupFormData.archiveDir) {
    fileArchiveFields["Archive Directory"] = backupFormData.archiveDir;
  }

  // Add S3 archive directory (separate from credential ID)
  if (backupFormData.s3ArchiveDir) {
    fileArchiveFields["S3 Archive Directory"] = backupFormData.s3ArchiveDir;
  }

  // Add Google Storage archive directory (separate from credential ID)
  if (backupFormData.gsArchiveDir) {
    fileArchiveFields["Google Storage Archive Directory"] =
      backupFormData.gsArchiveDir;
  }

  // Store archive credential ID
  // The backend uses archive_cred_id for either S3 or GS based on which archive_dir is present
  // If both are present, prioritize S3 credential ID
  const s3CredentialId = backupFormData.s3ArchiveCredentialId;
  const gsCredentialId = backupFormData.gsArchiveCredentialId;

  if (
    backupFormData.s3ArchiveDir &&
    s3CredentialId !== undefined &&
    s3CredentialId !== null &&
    s3CredentialId !== "-1" &&
    Number(s3CredentialId) !== -1
  ) {
    fileArchiveFields["Archive Key ID"] = String(s3CredentialId);
  } else if (
    backupFormData.gsArchiveDir &&
    gsCredentialId !== undefined &&
    gsCredentialId !== null &&
    gsCredentialId !== "-1" &&
    Number(gsCredentialId) !== -1
  ) {
    fileArchiveFields["Archive Key ID"] = String(gsCredentialId);
  }

  return fileArchiveFields;
}

export function buildVmetaCredentialFields(params: {
  provider: DataSourceProvider;
  credentialId?: number | null;
  serviceTokenId?: number | null;
}): Record<string, string> {
  const { provider, credentialId, serviceTokenId } = params;
  const fields: Record<string, string> = {};

  if (
    provider === DataSourceProvider.Amazon &&
    credentialId &&
    credentialId !== -1
  ) {
    fields["AWS S3 Key ID"] = String(credentialId);
  }

  if (
    provider === DataSourceProvider.Google &&
    credentialId &&
    credentialId !== -1
  ) {
    fields["Google Storage Key ID"] = String(credentialId);
  }

  if (
    provider === DataSourceProvider.BaseSpace &&
    serviceTokenId &&
    serviceTokenId !== -1
  ) {
    fields["Service Token ID"] = String(serviceTokenId);
  }

  return fields;
}

/**
 * The `dataSource` block of a dataset addition. For SSH it also names the run environment the
 * files were browsed on, so the backend can hold an agent run environment's additions inside its
 * data folders even when no bookmarked data source is named.
 */
export function buildSaveFileDataSource(params: {
  provider: DataSourceProvider;
  dataSourceId?: number | null;
  serviceTokenId?: number;
  runEnvId?: number;
}): SaveFileRequestBody["dataSource"] {
  const dataSource: SaveFileRequestBody["dataSource"] = {
    provider: params.provider,
  };
  if (params.dataSourceId) dataSource.id = params.dataSourceId;
  if (params.provider === DataSourceProvider.BaseSpace)
    dataSource.serviceTokenId = params.serviceTokenId;
  if (params.provider === DataSourceProvider.SSH && params.runEnvId)
    dataSource.runEnvId = params.runEnvId;
  return dataSource;
}

/** Labels and names of the file fields that record where a file came from (agent-5-5). */
export const SOURCE_NAME_LABEL = "Source Name";
export const SOURCE_LABEL = "Source";
const SYSTEM_SOURCE_FIELDS = [
  { label: SOURCE_NAME_LABEL, name: "data_source_name" },
  { label: SOURCE_LABEL, name: "data_source_provider" },
];

export type FileFieldNameLabel = { name: string; label: string };

/**
 * True when both source labels belong to the system fields on this canvas: each label is used,
 * and only by its system name. The backend maps a label keyed body to field names by label, so on
 * a canvas where a user's own field is labelled "Source" a source key would write into it.
 */
export function hasSystemSourceFields(
  fileFields: FileFieldNameLabel[] | null | undefined
): boolean {
  if (!fileFields) return false;
  return SYSTEM_SOURCE_FIELDS.every(({ label, name }) => {
    const withLabel = fileFields.filter(field => field.label === label);
    return (
      withLabel.length > 0 && withLabel.every(field => field.name === name)
    );
  });
}

/**
 * The source of a file being added, keyed by LABEL like every other key in an addFile body, or
 * nothing at all when the canvas's field list is unknown or does not have the system fields. Both
 * keys are gated together, so a file never carries half a snapshot.
 */
export function buildVmetaSourceFields(params: {
  provider: DataSourceProvider;
  dataSourceName?: string | null;
  fileFields: FileFieldNameLabel[] | null | undefined;
}): Record<string, string> {
  if (!hasSystemSourceFields(params.fileFields)) return {};
  const providerKey = DataSourceProvider[params.provider];
  if (typeof providerKey !== "string") return {};
  const fields: Record<string, string> = { [SOURCE_LABEL]: providerKey };
  const name = params.dataSourceName?.trim();
  if (name) fields[SOURCE_NAME_LABEL] = name;
  return fields;
}

/** One file of the PremiumSampleOrganizer save, exactly as it is posted to addFile. */
export function buildPremiumFilePayload(params: {
  file: Record<string, unknown>;
  archiveFields: Record<string, unknown>;
  provider: DataSourceProvider;
  credentialId?: number | null;
  serviceTokenId?: number | null;
  dataSourceName?: string | null;
  fileFields: FileFieldNameLabel[] | null | undefined;
}): Record<string, unknown> {
  const { file, archiveFields, provider, credentialId, serviceTokenId } =
    params;
  const providerFields =
    provider === DataSourceProvider.BaseSpace
      ? { Provider: FileProvider.BaseSpace }
      : {};
  return {
    ...file,
    ...archiveFields,
    ...buildVmetaCredentialFields({ provider, credentialId, serviceTokenId }),
    ...providerFields,
    // Last, so the snapshot always wins over anything the row carried.
    ...buildVmetaSourceFields({
      provider,
      dataSourceName: params.dataSourceName,
      fileFields: params.fileFields,
    }),
  };
}

/** The add folder path modal's addFile body. A pasted path has no bookmark, so no name. */
export function buildFolderPathFilePayload(params: {
  path: string;
  name: string;
  provider: DataSourceProvider | null;
  credentialId: number | null;
  fileFields: FileFieldNameLabel[] | null | undefined;
}): Record<string, unknown> {
  const { path, name, provider, credentialId, fileFields } = params;
  return {
    ...(name ? { Name: name } : {}),
    File1: path,
    ...(provider != null
      ? buildVmetaCredentialFields({ provider, credentialId })
      : {}),
    ...(provider != null
      ? buildVmetaSourceFields({ provider, fileFields })
      : {}),
  };
}

/** The name of the bookmark a data source page was opened from, when the list has it. */
export function findBookmarkName(
  list: Pick<DataSource, "id" | "name">[] | undefined,
  bookmarkId: string | null
): string | undefined {
  if (!bookmarkId || !list) return undefined;
  return list.find(item => item.id === Number(bookmarkId))?.name;
}
